import React from "react";
import { CheckCircle2, Phone, ShieldCheck, Clock, Wrench, Handshake } from "lucide-react";
import { Section, Overline, H1, Lead, H2 } from "@/components/ui-kit";
import MultiStepSellerForm from "@/components/forms/MultiStepSellerForm";

const SITUATIONS = [
  "Inherited property",
  "Tenant issues",
  "Deferred maintenance",
  "Vacant property",
  "Behind on payments",
  "Complex ownership",
  "Major repairs needed",
  "Need flexible closing",
  "Just exploring options",
];

const STRUCTURES = [
  { icon: Handshake, t: "Cash purchase", b: "Direct close, fewer surprises." },
  { icon: Wrench, t: "As-is purchase", b: "No clean-up, no repair list." },
  { icon: Clock, t: "Flexible closing", b: "Match your timeline, not ours." },
  { icon: ShieldCheck, t: "Seller financing", b: "Tax-aware, structured around your goals." },
  { icon: Handshake, t: "Creative terms", b: "Built around the situation in front of us." },
  { icon: Phone, t: "Partnership-style", b: "When that's the better outcome." },
];

const TRUST_BULLETS = [
  "No obligation",
  "No repairs required",
  "Flexible timelines",
  "Direct operator review",
  "We do not sell your information",
];

export default function Sell() {
  return (
    <>
      <section className="relative">
        <div className="absolute inset-0 bp-grid-fine opacity-40" />
        <Section className="pt-16 md:pt-24 pb-10 relative">
          <Overline>Sell Your Property</Overline>
          <H1 className="mt-5 max-w-4xl">Sell your property on your terms. Fast, flexible, and straightforward.</H1>
          <Lead className="mt-7 max-w-3xl">
            Tell us about the property and your situation. We'll review it directly and contact you with practical
            next-step options.
          </Lead>
        </Section>
      </section>

      <Section className="pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          <div className="lg:col-span-1 space-y-8">
            <div>
              <div className="overline">Why sellers work with us</div>
              <ul className="mt-5 space-y-3">
                {TRUST_BULLETS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-neutral-200">
                    <CheckCircle2 className="text-[#C19B6C] flex-shrink-0 mt-0.5" size={16} strokeWidth={1.6} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-frame p-5 border-l-4 border-l-[#C19B6C]">
              <p className="text-sm text-neutral-200 leading-relaxed">
                Every property is reviewed directly by an operator, not an acquisition call center.
              </p>
            </div>
          </div>
          <div className="lg:col-span-2" id="sell-form-section" data-testid="sell-form-section">
            <MultiStepSellerForm />
          </div>
        </div>
      </Section>

      <Section className="py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <Overline>Situations We Handle</Overline>
            <H2 className="mt-3 text-2xl">Real situations. Direct review.</H2>
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {SITUATIONS.map((s) => (
                <li key={s} className="text-sm text-neutral-300 border-l border-[#C19B6C]/50 pl-4 py-1">{s}</li>
              ))}
            </ul>
          </div>
          <div>
            <Overline>Ways We Can Structure Deals</Overline>
            <H2 className="mt-3 text-2xl">Built around your goals: not a fixed script.</H2>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {STRUCTURES.map((s) => (
                <div key={s.t} className="card-frame p-5">
                  <s.icon className="text-[#C19B6C]" size={20} strokeWidth={1.4} />
                  <h4 className="font-display text-base mt-4">{s.t}</h4>
                  <p className="text-xs text-neutral-400 mt-2 leading-relaxed">{s.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* DECISION-STAGE CTA */}
      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-20 text-center">
          <H2 className="text-2xl max-w-3xl mx-auto">Ready to see what's possible for your property?</H2>
          <p className="text-neutral-400 mt-4 max-w-xl mx-auto">No pressure. No obligation. Direct review by an operator.</p>
          <a href="#sell-form-section" className="btn-gold inline-block mt-8 px-7 py-4 font-semibold">See what options might make sense for your situation</a>
        </div>
      </section>
    </>
  );
}
